import React from 'react'
import { Card } from '../../../ui'
import { QuickPulseSparkline, SparklineLoading, SparklineError } from './QuickPulseSparkline.jsx'

const PREP_COLOR = '#3b82c4'

export const CARD_HISTORY_CONFIGS = {
  'prep-energy': {
    title: 'Energy over time',
    colors: { 1: '#e85d5d', 2: '#e89a5d', 3: '#c9b458', 4: '#7eb86a', 5: '#4a9e6a' },
    labels: { 1: 'Critical', 2: 'Low', 3: 'Neutral', 4: 'Good', 5: 'On fire' },
    zones: [
      { min: 1, max: 1.5, color: '#e85d5d' },
      { min: 1.5, max: 2.5, color: '#e89a5d' },
      { min: 2.5, max: 3.5, color: '#c9b458' },
      { min: 3.5, max: 4.5, color: '#7eb86a' },
      { min: 4.5, max: 5, color: '#4a9e6a' },
    ],
  },
  'prep-week-rating': {
    title: 'Week rating over time',
    colors: { 1: '#E24B4A', 2: '#D85A30', 3: '#BA7517', 4: '#639922', 5: '#1D9E75' },
    labels: { 1: 'Rough', 2: 'Tough', 3: 'Mixed', 4: 'Good', 5: 'Great' },
    zones: [
      { min: 1, max: 1.5, color: '#E24B4A' },
      { min: 1.5, max: 2.5, color: '#D85A30' },
      { min: 2.5, max: 3.5, color: '#BA7517' },
      { min: 3.5, max: 4.5, color: '#639922' },
      { min: 4.5, max: 5, color: '#1D9E75' },
    ],
  },
  'prep-workload': {
    title: 'Workload over time',
    colors: { 1: '#E24B4A', 2: '#BA7517', 3: '#1D9E75', 4: '#BA7517', 5: '#E24B4A' },
    labels: { 1: 'Underloaded', 2: 'Light', 3: 'Just right', 4: 'Heavy', 5: 'Overloaded' },
    zones: [
      { min: 1, max: 1.5, color: '#E24B4A' },
      { min: 1.5, max: 2.5, color: '#BA7517' },
      { min: 2.5, max: 3.5, color: '#1D9E75' },
      { min: 3.5, max: 4.5, color: '#BA7517' },
      { min: 4.5, max: 5, color: '#E24B4A' },
    ],
  },
  'prep-clarity': {
    title: 'Clarity over time',
    colors: { 1: '#E24B4A', 2: '#D85A30', 3: '#BA7517', 4: '#639922', 5: '#1D9E75' },
    labels: { 1: 'Unclear', 2: 'Mostly unclear', 3: 'Finding focus', 4: 'Mostly clear', 5: 'Crystal clear' },
    zones: [
      { min: 1, max: 1.5, color: '#E24B4A' },
      { min: 1.5, max: 2.5, color: '#D85A30' },
      { min: 2.5, max: 3.5, color: '#BA7517' },
      { min: 3.5, max: 4.5, color: '#639922' },
      { min: 4.5, max: 5, color: '#1D9E75' },
    ],
  },
}

function avgColor(avg, zones) {
  for (const zone of zones) {
    if (avg >= zone.min && avg < zone.max) return zone.color
  }
  return zones[zones.length - 1].color
}

export function CardHistoryBack({ cardDef, entries, loading, error, onFlip }) {
  const config = CARD_HISTORY_CONFIGS[cardDef.id] || CARD_HISTORY_CONFIGS['prep-energy']
  const avg = entries && entries.length > 0
    ? entries.reduce((sum, e) => sum + e.value, 0) / entries.length
    : null

  return (
    <Card
      variant="default"
      style={{
        padding: 0,
        minHeight: 216,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <button
        onClick={onFlip}
        onMouseDown={(e) => e.stopPropagation()}
        onDragStart={(e) => e.preventDefault()}
        aria-label={`Back to ${cardDef.label}`}
        style={styles.headerBtn}
      >
        <span style={{ ...styles.iconBadge, background: PREP_COLOR + '18', color: PREP_COLOR }}>
          {cardDef.icon ?? '⚡'}
        </span>
        <div style={styles.headerText}>
          <span style={styles.label}>{config.title}</span>
          <span style={styles.prompt}>Click to go back</span>
        </div>
      </button>

      <div style={{ ...styles.accentBar, background: PREP_COLOR }} />

      <div style={styles.chartArea}>
        {loading && <SparklineLoading />}
        {error && <SparklineError />}
        {!loading && !error && entries && entries.length > 0 && (
          <QuickPulseSparkline entries={entries} colors={config.colors} labels={config.labels} zones={config.zones} />
        )}
        {!loading && !error && entries && entries.length === 0 && (
          <div style={styles.empty}>No history yet</div>
        )}
      </div>

      {avg !== null && !loading && !error && (
        <div style={styles.footer}>
          <div style={styles.summaryRow}>
            <span style={styles.sessionCount}>{entries.length} sessions</span>
            <span style={styles.dot}>·</span>
            <span style={styles.avgLabel}>Avg</span>
            <div style={styles.barTrack}>
              {config.zones.map((zone, i) => {
                const left = ((zone.min - 1) / 4) * 100
                const width = ((zone.max - zone.min) / 4) * 100
                return (
                  <div
                    key={i}
                    style={{
                      position: 'absolute',
                      left: `${left}%`,
                      width: `${width}%`,
                      height: '100%',
                      background: zone.color,
                      opacity: 0.35,
                      borderRadius:
                        i === 0
                          ? '100px 0 0 100px'
                          : i === config.zones.length - 1
                            ? '0 100px 100px 0'
                            : '0',
                    }}
                  />
                )
              })}
              <div
                style={{
                  position: 'absolute',
                  left: `${((avg - 1) / 4) * 100}%`,
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  background: avgColor(avg, config.zones),
                  border: '1.5px solid var(--surface, #fff)',
                  boxShadow: '0 0 0 1px ' + avgColor(avg, config.zones),
                  transition: 'left 0.3s ease-out',
                }}
              />
            </div>
            <span style={styles.avgValue}>{avg.toFixed(1)}</span>
          </div>
        </div>
      )}
    </Card>
  )
}

const styles = {
  headerBtn: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '12px 12px 0',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
  },
  iconBadge: {
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    flexShrink: 0,
  },
  headerText: { display: 'flex', flexDirection: 'column', gap: '1px' },
  label: { fontSize: '12px', fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--text-primary)' },
  prompt: { fontSize: '11px', color: 'var(--text-secondary)', opacity: 0.8 },
  accentBar: {
    height: '3px',
    opacity: 0.25,
    margin: '6px 12px',
    borderRadius: '2px',
  },
  chartArea: {
    flex: 1,
    padding: '0 8px',
    minHeight: 0,
    overflow: 'hidden',
  },
  empty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    fontSize: '11px',
    color: 'var(--text-secondary)',
  },
  footer: {
    padding: '6px 12px 10px',
    borderTop: '1px solid var(--border)',
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  sessionCount: {
    fontSize: '11px',
    color: 'var(--text-secondary)',
  },
  dot: {
    fontSize: '11px',
    color: 'var(--text-secondary)',
    opacity: 0.5,
  },
  avgLabel: {
    fontSize: '11px',
    color: 'var(--text-secondary)',
  },
  barTrack: {
    flex: 1,
    height: 5,
    background: 'var(--border)',
    borderRadius: '100px',
    overflow: 'visible',
    position: 'relative',
  },
  avgValue: {
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    minWidth: '24px',
    textAlign: 'right',
  },
}
